import { useTranslation } from "react-i18next";
import { useOnboardingWizard } from "./OnboardingWizardProvider";
import {
  Trophy,
  User,
  Building2,
  MessageSquare,
  Upload,
  Star,
  Lock,
} from "lucide-react";

const achievementIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  User,
  Building2,
  MessageSquare,
  Upload,
  Trophy,
};

export function GamificationSidebar() {
  const { t } = useTranslation();
  const { totalPoints, maxPoints, unlockedAchievements, achievements } =
    useOnboardingWizard();
  const progressPercent = (totalPoints / maxPoints) * 100;

  return (
    <div className="flex flex-col h-full p-6 space-y-8">
      {/* Logo / Brand */}
      <div className="space-y-1">
        <h2 className="text-lg font-bold text-foreground">NARA</h2>
        <p className="text-sm text-muted-foreground">
          {t("onboarding.wizard.sidebar.setup")}
        </p>
      </div>

      {/* XP Progress */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-bold text-foreground">
            {totalPoints}
          </span>
          <span className="text-sm text-muted-foreground">
            / {maxPoints} XP
          </span>
        </div>
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${progressPercent}%`,
              background: "linear-gradient(90deg, #F69E47, #F8C552)",
            }}
          />
        </div>
      </div>

      {/* Achievements */}
      <div className="space-y-4 flex-1">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {t("onboarding.wizard.sidebar.achievements")}
        </h3>
        <div className="space-y-3">
          {achievements.map((achievement) => {
            const isUnlocked = unlockedAchievements.includes(achievement.id);
            const Icon = achievementIcons[achievement.icon] || Star;

            return (
              <div
                key={achievement.id}
                className={`
                  flex items-center gap-3 p-3 rounded-xl border transition-all duration-300
                  ${
                    isUnlocked
                      ? "border-nara-gold/50 bg-nara-gold/10"
                      : "border-border bg-muted/50 opacity-60"
                  }
                `}
              >
                <div
                  className={`
                  w-9 h-9 rounded-lg flex items-center justify-center shrink-0
                  ${isUnlocked ? "bg-nara-gold/20" : "bg-muted"}
                `}
                >
                  {isUnlocked ? (
                    <Icon className="h-4 w-4 text-nara-gold" />
                  ) : (
                    <Lock className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0">
                  <p
                    className={`text-sm font-medium truncate ${isUnlocked ? "text-foreground" : "text-muted-foreground"}`}
                  >
                    {t(achievement.title)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    +{achievement.points} XP
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
